import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { DEEPLINK_PARAMS } from '../../shared/constants/deeplinkParams';

export enum PLAN_CATEGORY {
	MONTHLY_DECLARATIONS = 'monthly_declarations',
	REGULARIZATIONS = 'regularizations',
	ANNUAL_DECLARATIONS = 'annual_declarations',
	ASAT = 'asat',
	INVOICING = 'invoicing',
}

export const PLAN_SECTION_SLUG = {
	SUBSCRIPTIONS: 'subscriptions',
	ANNUALS: 'annuals',
	REGULARIZATIONS: 'regularizations',
	ASAT: 'asat',
	INVOICING: 'invoicing',
} as const;

export type PlanSectionSlug =
	(typeof PLAN_SECTION_SLUG)[keyof typeof PLAN_SECTION_SLUG];

export const DEFAULT_PLAN_SECTION: PlanSectionSlug =
	PLAN_SECTION_SLUG.SUBSCRIPTIONS;

export const SECTION_TO_CATEGORY: Record<PlanSectionSlug, PLAN_CATEGORY> = {
	[PLAN_SECTION_SLUG.SUBSCRIPTIONS]: PLAN_CATEGORY.MONTHLY_DECLARATIONS,
	[PLAN_SECTION_SLUG.ANNUALS]: PLAN_CATEGORY.ANNUAL_DECLARATIONS,
	[PLAN_SECTION_SLUG.REGULARIZATIONS]: PLAN_CATEGORY.REGULARIZATIONS,
	[PLAN_SECTION_SLUG.ASAT]: PLAN_CATEGORY.ASAT,
	[PLAN_SECTION_SLUG.INVOICING]: PLAN_CATEGORY.INVOICING,
};

export const CATEGORY_TO_SECTION: Record<PLAN_CATEGORY, PlanSectionSlug> = {
	[PLAN_CATEGORY.MONTHLY_DECLARATIONS]: PLAN_SECTION_SLUG.SUBSCRIPTIONS,
	[PLAN_CATEGORY.ANNUAL_DECLARATIONS]: PLAN_SECTION_SLUG.ANNUALS,
	[PLAN_CATEGORY.REGULARIZATIONS]: PLAN_SECTION_SLUG.REGULARIZATIONS,
	[PLAN_CATEGORY.ASAT]: PLAN_SECTION_SLUG.ASAT,
	[PLAN_CATEGORY.INVOICING]: PLAN_SECTION_SLUG.INVOICING,
};

export const isValidSection = (
	value: string | undefined,
): value is PlanSectionSlug =>
	!!value && Object.values(PLAN_SECTION_SLUG).includes(value as PlanSectionSlug);

const resolvePlanSectionRedirect = (
	url: URL,
	section: string | undefined,
): string | null => {
	const params = new URLSearchParams(url.search);
	const legacyCategory = params.get(DEEPLINK_PARAMS.PLAN_CATEGORY);
	if (legacyCategory) {
		params.delete(DEEPLINK_PARAMS.PLAN_CATEGORY);
		const target =
			CATEGORY_TO_SECTION[legacyCategory as PLAN_CATEGORY] ??
			DEFAULT_PLAN_SECTION;
		const query = params.toString();
		return `/plans/${target}${query ? `?${query}` : ''}`;
	}
	if (!isValidSection(section)) {
		const query = params.toString();
		return `/plans/${DEFAULT_PLAN_SECTION}${query ? `?${query}` : ''}`;
	}
	return null;
};

/**
 * Normaliza toda URL de /plans en el data layer: sin sección o sección
 * inválida → sección default; deeplink legacy ?plan_category= → su sección,
 * preservando el resto del query. Debe ser un loader (no un <Navigate> en el
 * componente): una navegación disparada desde un effect en el primer mount de
 * una ruta lazy durante el boot se descarta silenciosamente y deja la
 * pantalla en blanco.
 */
export const planSectionLoader = ({ request, params }: LoaderFunctionArgs) => {
	const target = resolvePlanSectionRedirect(new URL(request.url), params.section);
	return target ? redirect(target) : null;
};
